import { Box, Skeleton } from '@mui/material';
import React, { MutableRefObject, ReactNode, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useBundleTranslation from 'i18n';
import { useIsConfigConstantEqual } from 'hooks/useConfigConstant';
import { ExternalContentDisplayType } from 'components/element-viewer';

function useAdjustIframeLayout(
    iframeRef: MutableRefObject<HTMLIFrameElement | undefined>,
    iframeLoaded: boolean,
    imageSize?: { height: number; width: number },
): [number, number] {
    const [resultHeight, setResultHeight] = useState(0);
    const [resultWidth, setResultWidth] = useState(0);
    const adjustIframeHeight = function () {
        if (!imageSize || imageSize?.width == 0 || !iframeRef.current) {
            setResultHeight(0);
            return;
        }
        let height = 650;
        const src = iframeRef.current.src;
        const iWidth = iframeRef.current.getBoundingClientRect().width;
        if ('undefined' !== typeof src && src && -1 !== src.indexOf('index/intermediate/')) {
            height = Math.floor((imageSize.height / imageSize.width) * iWidth) + 1;
        } else {
            height = Math.floor(((imageSize.height + 75) / imageSize.width) * iWidth) + 35;
        }

        let width = Math.min(Math.max(1128, imageSize.width * (1 + 75 / imageSize.height)), 32700);

        setResultHeight(height < 500 ? 500 : height);
        setResultWidth(width);
    };
    useEffect(() => {
        const onResize = () => {
            adjustIframeHeight();
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [imageSize, iframeRef]);

    useEffect(() => {
        adjustIframeHeight();
    }, [iframeRef.current, imageSize, iframeLoaded]);

    return [resultHeight, resultWidth];
}

export default function EmbedElement({
    embedUrl,
    displayType,
    url,
    iframeHeight,
    iframeWidth,
    fullScreen,
    imageSize,
    children,
    refreshFrequency,
}: {
    embedUrl: string;
    displayType: ExternalContentDisplayType;
    fullScreen?: boolean;
    url?: string;
    iframeHeight?: number;
    iframeWidth?: number;
    imageSize?: {
        height: number;
        width: number;
    };
    children?: ReactNode;
    refreshFrequency?: number;
}) {
    const [isLoaded, setIsLoaded] = useState(false);
    const handleOnLoad = (e: any) => {
        if (displayType == 'image') {
            if (imgRef?.current?.complete) {
                setIsLoaded(true);
            }
        } else {
            if (e?.target?.src != window.location.href && e?.target?.src != encodeURI(window.location.href)) {
                setIsLoaded(true);
            }
        }
    };

    useEffect(() => {
        setIsLoaded(false);
    }, [embedUrl]);

    const imgRef = useRef<HTMLImageElement>(null);
    const image = (
        <img
            ref={imgRef}
            onLoad={handleOnLoad}
            onError={handleOnLoad}
            style={{ minHeight: '650px', maxWidth: '100%' }}
            src={embedUrl}
        />
    );

    const [searchParams] = useSearchParams();
    const fullHeight = displayType == 'image' || searchParams.has('fullHeight');
    // Default Height
    let height = fullScreen ? '100%' : '650px';
    let width = '100%';
    if (fullHeight) {
        height = '100%';
    } else if (displayType == 'iframe' && iframeHeight) {
        height = iframeHeight + 'px';
    }

    if (displayType == 'iframe' && iframeWidth) {
        width = iframeWidth + 'px';
    }

    const { t } = useBundleTranslation(['components/report-content']);

    const iframeRef = useRef<HTMLIFrameElement>();

    const [adjustHeight, adjustWidth] = useAdjustIframeLayout(
        iframeRef,
        isLoaded,
        displayType == 'iframe' ? imageSize : undefined,
    );
    if (!fullHeight && adjustHeight > 0) {
        height = adjustHeight + 'px';
    }
    if (adjustWidth > 0) {
        width = adjustWidth + 'px';
    }

    const isAIClipboard = useIsConfigConstantEqual('ALLOW_IFRAME_CLIPBOARD_READ_WRITE', true);

    const refreshInterval = useRef(0);
    useEffect(() => {
        if (!refreshFrequency || refreshFrequency <= 0 || !iframeRef.current) {
            return;
        }
        if (refreshInterval.current) {
            clearInterval(refreshInterval.current);
        }
        refreshInterval.current = window.setInterval(
            () => {
                if (iframeRef?.current?.src) {
                    // Will trigger iframe reload
                    iframeRef.current.src = iframeRef.current.src;
                }
            },
            refreshFrequency * 60 * 1000,
        );

        return () => {
            if (refreshInterval.current) {
                clearInterval(refreshInterval.current);
                refreshInterval.current = 0;
            }
        };
    }, [iframeRef.current, refreshFrequency]);

    return (
        <div
            data-test={'external-embed-element'}
            style={{
                width: fullScreen ? '100%' : width,
                height: fullScreen ? '100%' : height,
                overflow: 'auto',
                textAlign: 'center',
                position: 'relative',
                flexShrink: 0,
                minWidth: '100%',
            }}
        >
            {!isLoaded && (
                <Skeleton
                    sx={{ height: '100%', width: '100%', position: 'absolute' }}
                    animation="wave"
                    variant="rectangular"
                />
            )}
            {displayType == 'image' ? (
                url ? (
                    <a target={'_blank'} rel="noopener noreferrer" href={url}>
                        {image}
                    </a>
                ) : (
                    <span>{image}</span>
                )
            ) : (
                <Box
                    allow={
                        isAIClipboard
                            ? 'clipboard-read; clipboard-write; local-network-access *'
                            : 'local-network-access *'
                    }
                    ref={iframeRef}
                    id={'iframe-component'}
                    onLoad={handleOnLoad}
                    onError={handleOnLoad}
                    component="iframe"
                    width={width}
                    height={height}
                    src={embedUrl}
                    sx={{ border: 0, display: 'block', minHeight: iframeHeight ? undefined : '650px' }}
                />
            )}
            {isLoaded && children}
        </div>
    );
}
